<template>
  <div class="points-detail">
    <h1 class="page-title">积分明细</h1>
    
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>积分记录列表</h2>
        </div>
      </template>
      
      <div class="card-filter">
        <el-form :inline="true" :model="searchParams" label-position="left">
          <el-form-item label="用户搜索">
            <el-input
              v-model="searchParams.keyword"
              placeholder="请输入用户ID或名称"
              clearable
              @keyup.enter="fetchPointsHistory"
              style="width: 200px;"
            >
              <template #append>
                <el-button @click="fetchPointsHistory"><el-icon><Search /></el-icon></el-button>
              </template>
            </el-input>
          </el-form-item>
          
          <el-form-item>
            <el-button type="primary" @click="fetchPointsHistory">查询</el-button>
            <el-button @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
      </div>
      
      <el-table
        v-loading="loading"
        :data="pointsHistory"
        border
        style="width: 100%"
      >
        <el-table-column label="用户信息" width="200" align="left">
          <template #default="scope">
            <div class="user-info">
              <div class="user-name">{{ scope.row.user?.name || scope.row.user?.nickname || '-' }}</div>
              <div class="user-phone">{{ scope.row.user?.phone || '-' }}</div>
              <div class="user-level">{{ getMemberLevel(scope.row.user?.member_level) }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="type" label="积分类型" width="120" align="center">
          <template #default="scope">
            <el-tag :type="scope.row.type === 'income' ? 'success' : 'danger'">
              {{ scope.row.type === 'income' ? '收入' : '支出' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="amount" label="积分数" width="120" align="center">
          <template #default="scope">
            <span :class="scope.row.type === 'income' ? 'text-success' : 'text-danger'">
              {{ scope.row.type === 'income' ? '+' : '-' }}{{ scope.row.amount }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="source" label="积分来源" width="150" align="center">
          <template #default="scope">
            {{ getSourceLabel(scope.row.source) }}
          </template>
        </el-table-column>
        <el-table-column prop="reference_id" label="关联ID" width="150" align="center"></el-table-column>
        <el-table-column prop="description" label="说明" min-width="200" align="center"></el-table-column>
        <el-table-column prop="created_at" label="记录时间" width="200" align="center">
          <template #default="scope">
            {{ formatDate(scope.row.created_at) }}
          </template>
        </el-table-column>
      </el-table>
      
      <div class="card-footer">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Search } from '@element-plus/icons-vue';
import * as pointApi from '../../api/point';

// 积分来源映射
const sourceMap = {
  'room_fee': '房间费用',
  'system_consumption': '系统消费',
  'luky_cloud': 'Luky Cloud',
  'mall_discount': '商城折扣',
  'application': '积分申请'
};

// 加载状态
const loading = ref(false);

// 积分历史数据
const pointsHistory = ref([]);

// 搜索参数
const searchParams = ref({
  keyword: '',
  type: '',
  source: ''
});

// 分页数据
const currentPage = ref(1);
const pageSize = ref(10);
const total = ref(0);

// 获取积分来源标签
const getSourceLabel = (source) => {
  return sourceMap[source] || source || '-';
};

// 获取会员等级
const getMemberLevel = (level) => {
  const levels = {
    1: '普通会员',
    2: '银卡会员',
    3: '金卡会员',
    4: '铂金会员',
    5: '钻石会员'
  };
  return levels[level] || '普通会员';
};

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

// 获取积分历史
const fetchPointsHistory = async () => {
  loading.value = true;
  try {
    console.log('Fetching points history with params:', {
      page: currentPage.value,
      pageSize: pageSize.value,
      keyword: searchParams.value.keyword,
      type: searchParams.value.type,
      source: searchParams.value.source
    });
    
    const response = await pointApi.fetchPointsHistory({
      page: currentPage.value,
      pageSize: pageSize.value,
      keyword: searchParams.value.keyword,
      type: searchParams.value.type,
      source: searchParams.value.source
    });
    
    console.log('Points history response:', response);
    
    // 确保response是对象
    if (typeof response === 'object' && response !== null) {
      // 直接使用response.data和response.total，因为axios拦截器已经处理了响应
      pointsHistory.value = response.data || [];
      total.value = response.total || 0;
      console.log('Set pointsHistory:', pointsHistory.value);
      console.log('Set total:', total.value);
    } else {
      console.error('Invalid response format:', response);
      pointsHistory.value = [];
      total.value = 0;
    }
  } catch (error) {
    console.error('Error fetching points history:', error);
    ElMessage.error('获取积分记录失败：' + (error.message || '未知错误'));
    console.error('获取积分记录失败:', error);
    pointsHistory.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
};

// 分页大小变化
const handleSizeChange = (size) => {
  pageSize.value = size;
  currentPage.value = 1;
  fetchPointsHistory();
};

// 当前页变化
const handleCurrentChange = (page) => {
  currentPage.value = page;
  fetchPointsHistory();
};

// 重置搜索条件
const resetSearch = () => {
  searchParams.value = {
    keyword: '',
    type: '',
    source: ''
  };
  currentPage.value = 1;
  fetchPointsHistory();
};

// 页面加载时获取数据
onMounted(() => {
  fetchPointsHistory();
});
</script>

<style scoped>
.points-detail {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
}

.card-filter {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.card-footer {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.text-success {
  color: #67c23a;
  font-weight: bold;
}

.text-danger {
  color: #f56c6c;
  font-weight: bold;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.user-name {
  font-weight: 500;
  color: #333;
}

.user-phone {
  font-size: 12px;
  color: #909399;
}

.user-level {
  font-size: 12px;
  color: #606266;
}
</style>